import React from "react";
import type { KonvaEventObject } from "konva/lib/Node";
import { Group, Image as KonvaImage, Layer, Line, Rect, Stage, Text } from "react-konva";
import useImage from "use-image";

import { getCanvasBasePosition, getFitScale, roundCanvasValue } from "./geometry";
import {
  getSeatButtonShape,
  getSeatShapeGeometry,
  getSeatShapeTextOffset,
} from "./seatShapes";

export type LayoutNodeCanvasNode = {
  id: number;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  is_active?: boolean;
  meta?: Record<string, any> | null;
  fill?: string;
};

type LayoutNodeCanvasProps = {
  canvasWidth: number;
  canvasHeight: number;
  backgroundImageUrl?: string;
  nodes: LayoutNodeCanvasNode[];
  selectedNodeId?: number | null;
  allowDrag?: boolean;
  onSelect?: (nodeId: number) => void;
  onMove?: (nodeId: number, position: { x: number; y: number }) => void;
};

const DEFAULT_VIEWPORT = { width: 900, height: 520 };

const BackgroundImage: React.FC<{ url: string; width: number; height: number }> = ({ url, width, height }) => {
  const [image] = useImage(url, "anonymous");

  if (!image) {
    return null;
  }

  return <KonvaImage image={image} x={0} y={0} width={width} height={height} opacity={0.92} listening={false} />;
};

const LayoutNodeCanvas: React.FC<LayoutNodeCanvasProps> = ({
  canvasWidth,
  canvasHeight,
  backgroundImageUrl = "",
  nodes,
  selectedNodeId,
  allowDrag = false,
  onSelect,
  onMove,
}) => {
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = React.useState(DEFAULT_VIEWPORT);
  const draftLike = React.useMemo(
    () => ({ canvas_width: canvasWidth, canvas_height: canvasHeight }),
    [canvasHeight, canvasWidth],
  );
  const scale = getFitScale(draftLike as any, viewport);
  const origin = getCanvasBasePosition(draftLike as any, viewport, scale);

  React.useLayoutEffect(() => {
    const element = viewportRef.current;
    if (!element) {
      return undefined;
    }

    const updateViewport = () => {
      setViewport({
        width: Math.max(element.clientWidth || DEFAULT_VIEWPORT.width, 1),
        height: Math.max(element.clientHeight || DEFAULT_VIEWPORT.height, 1),
      });
    };

    updateViewport();

    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(updateViewport);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleDragEnd = (node: LayoutNodeCanvasNode, event: KonvaEventObject<DragEvent>) => {
    onMove?.(node.id, {
      x: roundCanvasValue(event.target.x()),
      y: roundCanvasValue(event.target.y()),
    });
  };

  return (
    <div ref={viewportRef} className="h-full min-h-[420px] w-full overflow-hidden rounded-lg bg-[#FFF7E8]">
      <Stage width={viewport.width} height={viewport.height} style={{ background: "#FFF7E8" }}>
        <Layer>
          <Group x={origin.x} y={origin.y} scaleX={scale} scaleY={scale}>
            <Rect x={0} y={0} width={canvasWidth} height={canvasHeight} fill="#FFFDF7" cornerRadius={24} />
            {backgroundImageUrl ? (
              <BackgroundImage url={backgroundImageUrl} width={canvasWidth} height={canvasHeight} />
            ) : null}
            {nodes.map((node) => {
              const isSelected = selectedNodeId === node.id;
              const seatShape = getSeatButtonShape(node.meta);
              const geometry = seatShape ? getSeatShapeGeometry(seatShape, node.width, node.height) : null;
              const fill = node.is_active === false ? "#CBD5E1" : node.fill || "#2563EB";
              const stroke = isSelected ? "#0F172A" : "#FFFFFF";
              const strokeWidth = isSelected ? 4 : 2;
              const textOffsetY = getSeatShapeTextOffset(seatShape, node.height);

              return (
                <Group
                  key={node.id}
                  x={node.x}
                  y={node.y}
                  rotation={node.rotation}
                  draggable={allowDrag}
                  onClick={() => onSelect?.(node.id)}
                  onTap={() => onSelect?.(node.id)}
                  onDragEnd={(event) => handleDragEnd(node, event)}
                >
                  {geometry?.kind === "polygon" ? (
                    <Line
                      points={geometry.points}
                      closed
                      fill={fill}
                      shadowBlur={isSelected ? 18 : 8}
                      shadowOpacity={0.18}
                      stroke={stroke}
                      strokeWidth={strokeWidth}
                    />
                  ) : (
                    <Rect
                      width={node.width}
                      height={node.height}
                      fill={fill}
                      cornerRadius={seatShape ? 2 : 18}
                      shadowBlur={isSelected ? 18 : 8}
                      shadowOpacity={0.18}
                      stroke={stroke}
                      strokeWidth={strokeWidth}
                    />
                  )}
                  <Text
                    width={node.width}
                    height={node.height}
                    y={textOffsetY}
                    text={node.label}
                    fontSize={14}
                    fontStyle="bold"
                    fill="#FFFFFF"
                    align="center"
                    verticalAlign="middle"
                  />
                </Group>
              );
            })}
          </Group>
        </Layer>
      </Stage>
    </div>
  );
};

export default LayoutNodeCanvas;
